import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";
import { useLocation } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";

const getFaqs = (location: any) => {
  const localContent = getLocalContent(location.name, location.regionSlug, 'es');
  return [
    { q: `¿Cuánto cuesta la limpieza del hogar en ${location.name}?`, a: `Las limpiezas estándar en ${location.name} empiezan desde $92. El precio depende del tamaño de tu hogar, el tipo de servicio y la frecuencia. Usa nuestra calculadora en línea para obtener tu precio exacto en segundos.` },
    ...localContent.uniqueFaqs.map(f => ({ q: f.question, a: f.answer })),
    { q: "¿La limpieza recurrente es más barata?", a: "¡Sí! El servicio semanal ahorra un 35%, quincenal un 30%, y mensual un 20% comparado con una sola limpieza." },
    { q: "¿Necesito estar en casa durante la limpieza?", a: "No es necesario. La mayoría de nuestros clientes no están en casa. Proporciona las instrucciones de acceso al momento de reservar y aseguramos tu hogar al terminar." },
    { q: "¿Están asegurados y certificados?", a: "Sí. Uplevel Cleaning está completamente licenciada, asegurada y bonificada en todas nuestras áreas de servicio." },
    { q: "¿En qué consiste la Garantía del 200%?", a: "Si no estás 100% satisfecho, regresamos a limpiar las áreas que se hayan pasado por alto — completamente gratis — dentro de las 48 horas. ¿Aún no estás satisfecho? Te devolvemos tu dinero completo, sin preguntas." },
    { q: "¿Cómo reservo un servicio?", a: "Reserva en línea en uplevelcleaning.com en menos de 60 segundos. Precios planos y transparentes, sin necesidad de llamar por teléfono." },
    { q: "¿Cuánto tiempo toma la limpieza?", a: "Normalmente entre 2 y 5 horas, dependiendo del tamaño y condición del hogar." },
    { q: "¿Los productos que usan son seguros para mascotas y niños?", a: "Sí. Utilizamos productos de limpieza profesionales que son seguros para mascotas y niños cuando se usan correctamente." },
    { q: "¿Puedo cancelar o reprogramar?", a: "Sí. Puedes cancelar o reprogramar con aviso previo sin ningún problema. Sin compromisos a largo plazo." },
  ];
};

function buildSpanishFAQSchema(faqs: any[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": "es",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };
}

export const SpanishFAQ = ({ cityName }: { cityName: string }) => {
  const { ref, isVisible } = useScrollAnimation();
  const location = useLocation();
  const faqs = getFaqs(location);

  // Inject JSON-LD FAQ rich snippet into <head> for Google indexing
  useEffect(() => {
    const id = "schema-es-faq";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("id", id);
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildSpanishFAQSchema(faqs));

    return () => {
      document.getElementById(id)?.remove();
    };
  }, [faqs]);

  return (
    <section className="py-24 bg-background">
      <div className="container max-w-4xl">
        <div ref={ref} className={cn("text-center mb-16 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Preguntas Frecuentes</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Todo lo que necesitas saber sobre nuestro servicio de limpieza en {cityName}.
          </p>
        </div>
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border rounded-xl px-6 bg-card shadow-sm">
              <AccordionTrigger className="text-left font-semibold text-base hover:text-primary transition-colors py-5">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
